import { Box, Button, Sheet, Stack, Typography, IconButton, Tooltip, Chip } from "@mui/joy";

import CachedRoundedIcon from '@mui/icons-material/CachedRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import ViewKanbanRoundedIcon from '@mui/icons-material/ViewKanbanRounded';

import { useContext, useState, useEffect } from "react";
import { LayoutContext } from "../../context";

import CalendarBlock from "./CalendarBlock";
import KanbanBoard from "./Kanban/KanbanBoard";
import LoadingBlockGif from "../../components/LoadingBlockGif";
import ApplicationsPanel from "../../components/ApplicationsPanel/ApplicationsPanel";
import ManagerDashboardBrief from "./ManagerDashboardBrief";
import QualityDashboardBrief from "./QualityDashboardBrief";
import QualityTeamOverview from "./QualityTeamOverview";


export default function ManagerPage() {
    const { loadListEvents, updateListEvents } = useContext(LayoutContext);
    // Канбан по умолчанию; запоминаем выбор менеджера между заходами.
    const [viewMode, setViewMode] = useState(() => {
        try { return localStorage.getItem('manager_view_mode') || 'kanban'; }
        catch (e) { return 'kanban'; }
    }); // 'calendar' | 'grid' | 'kanban'
    const [kanbanSummary, setKanbanSummary] = useState(null);

    // Брифы/овервью — вторичные виджеты. Монтируем их после первой отрисовки доски,
    // чтобы их запросы (manager-dashboard-brief, quality-dashboard-brief, team-overview,
    // statistics) не конкурировали со стартовой загрузкой канбана.
    const [briefsReady, setBriefsReady] = useState(false);
    useEffect(() => {
        const w = typeof window !== 'undefined' ? window : null;
        if (w && 'requestIdleCallback' in w) {
            const id = w.requestIdleCallback(() => setBriefsReady(true), { timeout: 2000 });
            return () => { try { w.cancelIdleCallback?.(id); } catch (e) {} };
        }
        const id = setTimeout(() => setBriefsReady(true), 400);
        return () => clearTimeout(id);
    }, []);

    // Запоминаем выбранный режим (канбан/календарь/сетка) между заходами.
    useEffect(() => {
        try { localStorage.setItem('manager_view_mode', viewMode); } catch (e) { /* ignore */ }
    }, [viewMode]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'flex-start',
                gap: 2,
                px: { xs: 2, md: 3 },
                // mt: { xs: 2, md: 2 },
                py: { xs: 2, sm: 2, md: 3 },
                width: 'auto',
                // height: '100%',
                minWidth: 0,
            }}
        >
            {/* ── Правая колонка ──────────────────────────────── */}
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                {briefsReady && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', lg: 'row' },
                        alignItems: 'flex-start',
                        gap: { xs: 0, lg: 2 },
                        width: '100%',
                    }}
                >
                    {/* Левая колонка: мои результаты — ограничиваем ширину */}
                    <Box sx={{ width: '100%', maxWidth: { lg: 460 }, flexShrink: 0 }}>
                        <ManagerDashboardBrief />
                        <QualityDashboardBrief />
                    </Box>

                    {/* Правая колонка: показатели менеджеров — занимает остаток */}
                    <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                        <QualityTeamOverview />
                    </Box>
                </Box>
                )}

                <Sheet
                    sx={{
                        borderRadius: 'xl',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 4px 24px rgba(87, 42, 195, 0.06)',
                        overflow: 'hidden',
                        bgcolor: 'background.surface',
                    }}
                >
                    {/* Шапка правой карточки — нейтральная, чтобы не спорить с фиолетовой панелью */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                            px: 2.5,
                            py: 1.75,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.level1',
                            flexWrap: 'wrap',
                            gap: 1,
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 'lg',
                                    bgcolor: 'primary.softBg',
                                    color: 'primary.500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <CalendarMonthRoundedIcon />
                            </Box>
                            <Box>
                                <Typography level="title-lg" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                    События
                                </Typography>
                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                    Календарь обмеров и работ
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                            {/* Успешные/неуспешные за месяц + % (только в канбане) */}
                            {viewMode === 'kanban' && kanbanSummary && (
                                <Stack direction="row" spacing={0.5} alignItems="center"
                                       sx={{ display: { xs: 'none', sm: 'flex' } }}>
                                    <Tooltip title="Успешные за месяц" variant="soft">
                                        <Chip size="sm" variant="soft" color="success" sx={{ fontWeight: 700 }}>
                                            ✓ {kanbanSummary.success_month ?? 0}
                                        </Chip>
                                    </Tooltip>
                                    <Tooltip title={kanbanSummary.success_rate_label || 'Доля успешных завершений за текущий месяц'}>
                                      <Chip size="sm" variant="solid" color="primary" sx={{ fontWeight: 800 }}>
                                        {kanbanSummary.success_rate_percent == null ? 'Нет завершений' : `${kanbanSummary.success_rate_percent}% успешных`}
                                      </Chip>
                                    </Tooltip>
                                    <Tooltip title="Неуспешные за месяц" variant="soft">
                                        <Chip size="sm" variant="soft" color="danger" sx={{ fontWeight: 700 }}>
                                            ✕ {kanbanSummary.unsuccess_month ?? 0}
                                        </Chip>
                                    </Tooltip>
                                </Stack>
                            )}
                            {/* Переключатель вида */}
                            <Sheet
                                variant="outlined"
                                sx={{
                                    display: 'flex',
                                    borderRadius: 'lg',
                                    overflow: 'hidden',
                                    p: 0.375,
                                    gap: 0.375,
                                    bgcolor: 'background.level1',
                                }}
                            >
                                <Tooltip title="Календарь" variant="soft">
                                    <IconButton
                                        size="sm"
                                        variant={viewMode === 'calendar' ? 'solid' : 'plain'}
                                        color={viewMode === 'calendar' ? 'primary' : 'neutral'}
                                        onClick={() => setViewMode('calendar')}
                                        sx={{ borderRadius: 'md', minWidth: 32, minHeight: 32 }}
                                    >
                                        <CalendarMonthRoundedIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Карточки" variant="soft">
                                    <IconButton
                                        size="sm"
                                        variant={viewMode === 'grid' ? 'solid' : 'plain'}
                                        color={viewMode === 'grid' ? 'primary' : 'neutral'}
                                        onClick={() => setViewMode('grid')}
                                        sx={{ borderRadius: 'md', minWidth: 32, minHeight: 32 }}
                                    >
                                        <GridViewRoundedIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Канбан" variant="soft">
                                    <IconButton
                                        size="sm"
                                        variant={viewMode === 'kanban' ? 'solid' : 'plain'}
                                        color={viewMode === 'kanban' ? 'primary' : 'neutral'}
                                        onClick={() => setViewMode('kanban')}
                                        sx={{ borderRadius: 'md', minWidth: 32, minHeight: 32 }}
                                    >
                                        <ViewKanbanRoundedIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                            </Sheet>

                            <Button
                                onClick={() => updateListEvents(null, null)}
                                startDecorator={<CachedRoundedIcon />}
                                color="neutral"
                                variant="soft"
                                size="sm"
                                sx={{ borderRadius: 'lg' }}
                            >
                                Обновить
                            </Button>
                        </Stack>
                    </Stack>

                    {/* Контент */}
                    <Box sx={{ p: { xs: 1, md: viewMode === 'kanban' ? 1 : 2.5 } }}>
                        {viewMode === 'kanban'
                            ? <KanbanBoard onSummary={setKanbanSummary} />
                            : <CalendarBlock viewMode={viewMode} />}
                    </Box>
                </Sheet>
            </Box>

            {/* ── Левая колонка ───────────────────────────────── */}
            {/* <Box sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' } }}>
                <ApplicationsPanel />
            </Box> */}
        </Box>
    );
}